/**
 * ADR-0242 — `AgentLogo`: one glyph for a visiting agent, everywhere.
 *
 * Resolution order (first hit wins):
 *   1. Sill's own tools → the Sill mark (`SILL_MARK_FOR_CLAIMS`, founder
 *      2026-10-13). A logo is a claim, like caller ID; the ✓ Confirmed
 *      mark is drawn separately and only from the server verdict.
 *   2. A bundled brand mark for the exact registry id (brand-marks.ts).
 *   3. The operator's own site icon, fetched and validated by Sill's API
 *      (agent-logo-store.ts) — loaded from Sill only, never hot-linked.
 *   4. A monogram tile: first letter of the operator / agent name, colour
 *      from a stable hash over a muted palette (never a semantic colour),
 *      so every row has a distinct visual even when nothing else exists.
 *   5. People → User glyph; nothing to name → generic Bot glyph.
 *
 * Mismatch and rejected rows never reach here with a brand: `visitLogo`
 * (visit-identity.ts) sends them to the coral bot / ShieldAlert glyphs.
 */

import { Bot, User, type LucideIcon } from 'lucide-react';

import { useTheme } from '../../lib/theme.js';
import { bundledBrandMark, bundledOrganisation, type SimpleIcon } from './brand-marks.js';
import { useFetchedAgentLogo } from './agent-logo-store.js';
import { SillMark } from './SillMark.js';
import { SILL_MARK_FOR_CLAIMS } from './visit-identity.js';

/** Sill's own tool ids (`agent_sill_readiness`, `agent_sill_detect`, …). */
export function isSillAgentId(agent_id: string | undefined | null): boolean {
  return typeof agent_id === 'string' && agent_id.startsWith('agent_sill_');
}

/** Relative luminance of a 6-char hex (0–1). */
function hexLuminance(hex: string): number {
  const h = hex.replace(/^#/, '');
  if (h.length !== 6) return 0.5;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Brand colours that would vanish on the current surface swap to the foreground. */
export function readableBrandFill(hex: string, theme: 'light' | 'dark'): string {
  const lum = hexLuminance(hex);
  if (theme === 'dark' && lum < 0.2) return '#E8E8E8';
  if (theme === 'light' && lum > 0.85) return '#181818';
  return `#${hex}`;
}

/** Stable 32-bit FNV-1a hash of a string. */
function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * Muted hues for monogram tiles. Low saturation in both themes so a tile
 * never reads as one of the semantic colours (lime approve, amber
 * escalate, coral reject, sky pending, mint confirmed).
 */
const MONOGRAM_HUES: readonly number[] = [210, 250, 280, 320, 20, 45, 160, 190];

export interface MonogramStyle {
  readonly letter: string;
  readonly background: string;
  readonly color: string;
  readonly border: string;
}

/** The letter and colours for a name; `null` when the name has no letter or digit. */
export function monogramStyle(name: string, theme: 'light' | 'dark'): MonogramStyle | null {
  const m = /[\p{L}\p{N}]/u.exec(name);
  if (m === null) return null;
  const hue = MONOGRAM_HUES[hash(name.trim().toLowerCase()) % MONOGRAM_HUES.length] as number;
  return theme === 'dark'
    ? { letter: m[0].toUpperCase(), background: `hsl(${hue}, 14%, 22%)`, color: `hsl(${hue}, 28%, 82%)`, border: `hsl(${hue}, 12%, 32%)` }
    : { letter: m[0].toUpperCase(), background: `hsl(${hue}, 24%, 92%)`, color: `hsl(${hue}, 32%, 30%)`, border: `hsl(${hue}, 18%, 82%)` };
}

/**
 * The name a monogram is drawn from: the registry organisation for a
 * bundled id (every OpenAI bot gets the same "O"), else the row's label,
 * else the id's own slug (`agent_novabot` → "novabot").
 */
export function monogramName(agent_id: string | null | undefined, label: string | undefined): string | null {
  const org = bundledOrganisation(agent_id);
  if (org !== null) return org.replace(/\s*\(.*\)\s*$/, '').trim();
  if (label !== undefined && label.trim().length > 0) return label.trim();
  if (typeof agent_id === 'string') {
    const slug = agent_id.replace(/^agent_/, '');
    return slug.length > 0 ? slug : null;
  }
  return null;
}

export function MonogramTile({ name, size }: { name: string; size: number }): JSX.Element | null {
  const { theme } = useTheme();
  const s = monogramStyle(name, theme);
  if (s === null) return null;
  return (
    <span
      role="img"
      aria-label={name}
      data-testid="agent-logo-monogram"
      style={{
        width: size,
        height: size,
        borderRadius: Math.max(2, Math.round(size * 0.18)),
        background: s.background,
        color: s.color,
        boxShadow: `inset 0 0 0 1px ${s.border}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Geist', sans-serif",
        fontSize: Math.max(8, Math.round(size * 0.62)),
        fontWeight: 600,
        lineHeight: 1,
        flexShrink: 0,
        userSelect: 'none',
      }}
    >
      {s.letter}
    </span>
  );
}

function SimpleIconSvg({ icon, size }: { icon: SimpleIcon; size: number }): JSX.Element {
  const { theme } = useTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label={icon.title} data-testid="agent-logo-brand" style={{ flexShrink: 0 }}>
      <path d={icon.path} fill={readableBrandFill(icon.hex, theme)} />
    </svg>
  );
}

function FetchedLogo({ src, size, name }: { src: string; size: number; name: string }): JSX.Element {
  const { theme } = useTheme();
  // Site icons are drawn for light browser tabs; on the dark theme they sit
  // on a small light tile so dark glyphs stay visible.
  return (
    <span
      data-testid="agent-logo-fetched"
      style={{
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 2,
        background: theme === 'dark' ? 'rgba(255,255,255,0.92)' : 'transparent',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      <img
        src={src}
        alt={name}
        width={theme === 'dark' ? size - 2 : size}
        height={theme === 'dark' ? size - 2 : size}
        draggable={false}
        style={{ objectFit: 'contain', display: 'block' }}
      />
    </span>
  );
}

export interface AgentLogoProps {
  /** Registry agent id (bundled, curated or approved), or null. */
  agent_id: string | undefined | null;
  visitor_class?: 'matched_agent' | 'unknown_agent' | 'human_likely';
  size?: number;
  /** Colour for the generic lucide fallbacks. */
  fallbackColor?: string;
  /** Short display name for the row ("GPTBot", "SomeNewBot"); feeds the monogram. */
  label?: string;
}

export function AgentLogo({ agent_id, visitor_class, size = 14, fallbackColor, label }: AgentLogoProps): JSX.Element {
  const sill = isSillAgentId(agent_id);
  const brand = sill ? null : bundledBrandMark(agent_id);
  const fetched = useFetchedAgentLogo(!sill && brand === null ? agent_id : null);

  if (sill) {
    if (SILL_MARK_FOR_CLAIMS) return <SillMark size={size} />;
    return <Bot size={size} color={fallbackColor} style={{ flexShrink: 0 }} />;
  }
  if (brand !== null) {
    if (brand.kind === 'simple') return <SimpleIconSvg icon={brand.icon} size={size} />;
    const Glyph: LucideIcon = brand.icon;
    return <Glyph size={size} color={brand.color} aria-label={brand.title} style={{ flexShrink: 0 }} />;
  }
  const name = visitor_class === 'human_likely' && (agent_id === null || agent_id === undefined) ? null : monogramName(agent_id, label);
  if (fetched !== null) return <FetchedLogo src={fetched} size={size} name={name ?? 'Agent'} />;
  if (name !== null) {
    const tile = <MonogramTile name={name} size={size} />;
    // MonogramTile renders null only for a name with no letter or digit.
    if (monogramStyle(name, 'light') !== null) return tile;
  }
  const Fallback: LucideIcon = visitor_class === 'human_likely' ? User : Bot;
  return <Fallback size={size} color={fallbackColor} style={{ flexShrink: 0 }} />;
}
